"use client";

import { ListingContactForm } from "@/components/listings/sidebar/listing-contact-form";
import { ListingInquirySent } from "@/components/listings/sidebar/listing-inquiry-sent";
import { ListingInquiryDialog } from "@/components/listings/listing-inquiry-dialog";
import { Button } from "@/components/ui/button";
import { H4 } from "@/components/ui/heading";
import { Icon } from "@/components/ui/icon";
import { useInquirySender } from "@/hooks/use-inquiry-sender";
import { api } from "@/lib/api-client";
import type {
  Conversation,
  DetailedListing,
  DetailedListingViewAs,
} from "@/lib/types";
import { LoaderCircleIcon } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { useState, type ReactNode } from "react";

export type ListingSidebarProps = {
  listing: DetailedListing;
  /** Server-rendered `ListingPriceCard`; shown above the inquiry form. */
  priceCard?: ReactNode;
};

export function ListingSidebar({ listing, priceCard }: ListingSidebarProps) {
  const { user, loaded, senderError } = useInquirySender();
  const searchParams = useSearchParams();
  const [dialogOpen, setDialogOpen] = useState(false);
  const apiUtils = api.useUtils();

  const viewAs = searchParams.get("viewAs") as DetailedListingViewAs | null;
  const isOwner =
    !!user &&
    (user.id === listing.ownerId || user.id === listing.sellerAgentId);

  const isOwnerViewingAsBuyer =
    isOwner && (viewAs === "PUBLIC_BUYER" || viewAs === "SECURED_BUYER");

  const {
    data: conversationData,
    isFetching: conversationIsLoading,
    refetch: refetchConversation,
  } = api.listings.getListingInquiryConversation.useQuery(
    {
      id: listing.id,
    },
    {
      enabled: loaded && !!user && !isOwner,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  return (
    <>
      <section
        className="w-full rounded-2xl border border-border bg-card shadow-[0_6px_24px_rgba(0,0,0,0.06)] dark:shadow-[0_6px_24px_rgba(0,0,0,0.3)] md:sticky md:top-[calc(5rem_+_var(--banner-height))] md:self-start"
        id="listing-sidebar"
        aria-label="Asking price and inquiry"
      >
        {priceCard && (
          <div className="border-b border-border p-5">{priceCard}</div>
        )}

        {senderError && (
          <p role="alert" className="p-5 text-sm">
            Inquiry sender is unavailable. Choose an authorized workspace and
            try again.
          </p>
        )}
        {!loaded && (
          <div className="flex items-center justify-center py-12">
            <LoaderCircleIcon className="size-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {loaded && !user && !senderError && (
          <div className="flex w-full flex-col gap-3.5 p-5">
            <H4 className="text-base font-semibold">
              Interested in this business?
            </H4>
            <p className="text-sm text-muted-foreground">
              Send an inquiry directly to the broker. Most brokers respond
              within 24 hours.
            </p>
            <Button
              className="h-11 w-full bg-foreground text-background hover:bg-foreground/90"
              onClick={() => setDialogOpen(true)}
            >
              <Icon icon="MessagesSquare" />
              Send inquiry
            </Button>
          </div>
        )}

        {loaded && user && conversationIsLoading && (
          <div className="flex items-center justify-center py-12">
            <LoaderCircleIcon className="size-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {loaded &&
          user &&
          !conversationIsLoading &&
          (!isOwner || isOwnerViewingAsBuyer) &&
          conversationData?.conversation && (
            <ListingInquirySent
              conversation={conversationData.conversation as Conversation}
            />
          )}

        {loaded &&
          user &&
          !conversationIsLoading &&
          (!isOwner || isOwnerViewingAsBuyer) &&
          !conversationData?.conversation && (
            <ListingContactForm
              listing={listing}
              onSendComplete={refetchConversation}
            />
          )}
      </section>

      <ListingInquiryDialog
        listing={listing}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSent={() => {
          apiUtils.listings.getListingInquiryConversation.invalidate({
            id: listing.id,
          });
        }}
      />
    </>
  );
}

ListingSidebar.displayName = "ListingSidebar";
